import type { ReactNode, RefObject } from 'react';
import { create } from 'zustand';
import { fallbackContentRef } from '~/utils/fallback-content-ref';

type DialogContainerOptions = {
  ref: RefObject<HTMLDivElement | null>;
  overlay?: boolean;
  overlayRef?: RefObject<HTMLDivElement | null>;
};

export type TriggerRef = RefObject<HTMLButtonElement | HTMLAnchorElement | null>;

export type DialogData = {
  id: number | string;
  triggerRef: TriggerRef;
  description?: ReactNode;
  drawerOnMobile?: boolean;
  outsideScroll?: boolean;
  /** Remove immediately on dismissal so externally owned open state cannot flash the dialog back. */
  instantClose?: boolean;
  className?: string;
  headerClassName?: string;
  container?: DialogContainerOptions;
  title?: string | ReactNode;
  titleContent?: string | ReactNode;
  onClose?: (isCleanup?: boolean) => void;
};

export type InternalDialog = DialogData & {
  key: number;
  open?: boolean;
  content: ReactNode;
};

interface DialogStoreState {
  dialogs: InternalDialog[];

  create: (content: ReactNode, data: DialogData) => string | number;
  update: (id: number | string, updates: Partial<InternalDialog>) => void;
  remove: (id?: number | string, opts?: { isCleanup?: boolean }) => void;
  get: (id: number | string) => InternalDialog | undefined;
  scrollToTop: (id: number | string) => void;

  triggerRefs: Record<string, TriggerRef | null>;

  setTriggerRef: (id: string, ref: TriggerRef) => void;
  getTriggerRef: (id: string) => TriggerRef | null;
}

// Manages one or multiple dialogs; on mobile they render as drawers.
export const useDialoger = create<DialogStoreState>((set, get) => ({
  dialogs: [],
  triggerRefs: {},

  create: (content, data) => {
    // Blur the active element: a modal sets aria-hidden on ancestors and would trap focus there
    if (document.activeElement instanceof HTMLButtonElement || document.activeElement instanceof HTMLAnchorElement) {
      fallbackContentRef.current = document.activeElement;
      document.activeElement.blur();
    }

    const defaults = {
      drawerOnMobile: true,
      headerClassName: 'with-close-btn',
      open: true,
      modal: true,
      key: Date.now(),
    };

    set((state) => ({
      dialogs: [...state.dialogs.filter((d) => d.id !== data.id), { ...defaults, ...data, content }],
    }));

    return data.id;
  },

  update: (id, updates) => {
    set((state) => ({
      dialogs: state.dialogs.map((dialog) => (dialog.id === id ? { ...dialog, ...updates } : dialog)),
    }));
  },

  remove: (id, opts) => {
    const { dialogs } = get();
    const dialogsToRemove = id ? dialogs.filter((d) => d.id === id) : dialogs;
    if (!dialogsToRemove.length) return;

    // Update the store before onClose: a callback that navigates from inside set() would
    // interleave a router update with this one and render a stale frame of the dialog.
    set({ dialogs: dialogs.filter((d) => !dialogsToRemove.some((r) => r.id === d.id)) });

    for (const dialog of dialogsToRemove) dialog.onClose?.(opts?.isCleanup);
  },

  get: (id) => get().dialogs.find((d) => d.id === id),

  scrollToTop: (id) => {
    const popup = document.getElementById(String(id));
    popup?.closest('[data-slot="dialog-viewport"]')?.scrollTo({ top: 0 });
  },

  setTriggerRef: (id, ref) => {
    set((state) => ({
      triggerRefs: { ...state.triggerRefs, [id]: ref },
    }));
  },

  getTriggerRef: (id) => {
    return get().triggerRefs[id] ?? null;
  },
}));
